/**
 * Direct Twilio WhatsApp Integration Service
 * Bypasses Supabase Edge Functions and calls Twilio REST API directly
 */

class TwilioService {
  constructor() {
    // Get Twilio credentials from environment variables
    this.accountSid = import.meta.env?.VITE_TWILIO_ACCOUNT_SID;
    this.authToken = import.meta.env?.VITE_TWILIO_AUTH_TOKEN;
    this.whatsappFrom = import.meta.env?.VITE_TWILIO_WHATSAPP_FROM;
    this.verifyServiceSid = import.meta.env?.VITE_TWILIO_VERIFY_SERVICE_SID;
    
    // WhatsApp Template Configuration
    this.otpTemplateId = import.meta.env?.VITE_TWILIO_WHATSAPP_TEMPLATE_ID || 'HX999835db369a33272322bd02699e7c7b';
    this.otpTemplateName = import.meta.env?.VITE_TWILIO_WHATSAPP_TEMPLATE_NAME || 'otp_verification';
    
    // Validate configuration
    this.isConfigured = !!(this.accountSid && this.authToken && this.whatsappFrom);
    this.isVerifyConfigured = !!(this.accountSid && this.authToken && this.verifyServiceSid);
    
    if (!this.isConfigured) {
      console.warn('Twilio service not configured. Missing required environment variables.');
    }
  }

  /**
   * Check if Twilio service is properly configured
   */
  isServiceConfigured() {
    return this.isConfigured;
  }

  /**
   * Check if Twilio Verify service is properly configured
   */
  isVerifyServiceConfigured() {
    return this.isVerifyConfigured;
  }

  /**
   * Generate a 6-digit OTP code
   */
  generateOTP() {
    return Math.floor(100000 + Math.random() * 900000)?.toString();
  }

  /**
   * Format phone number to WhatsApp format
   */
  formatWhatsAppNumber(phoneNumber) {
    // Remove all non-digit characters
    let cleaned = phoneNumber?.replace(/\D/g, '');
    
    // Add country code if missing
    if (cleaned?.length === 10) {
      cleaned = '91' + cleaned; // India country code
    }
    
    // Ensure it starts with +
    if (!cleaned?.startsWith('+')) {
      cleaned = '+' + cleaned;
    }
    
    return `whatsapp:${cleaned}`;
  }

  /**
   * Send WhatsApp OTP message using approved WhatsApp template
   */
  async sendWhatsAppOTP(phoneNumber, otpCode) {
    if (this.isVerifyConfigured) {
      return this._sendVerifyWhatsAppOTP(phoneNumber);
    }

    if (!this.isConfigured) {
      throw new Error('Twilio service not configured. Please check environment variables.');
    }

    try {
      const toNumber = this.formatWhatsAppNumber(phoneNumber);
      
      console.log('Sending WhatsApp template message to:', toNumber?.substring(0, 15) + '...');

      // Prepare Twilio API request using Content Template
      const url = `https://api.twilio.com/2010-04-01/Accounts/${this.accountSid}/Messages.json`;
      
      const formData = new URLSearchParams();
      formData?.append('From', this.whatsappFrom);
      formData?.append('To', toNumber);
      
      // Use WhatsApp approved template instead of freestyle message
      formData?.append('ContentSid', this.otpTemplateId);
      
      // Add template variables (OTP code)
      formData?.append('ContentVariables', JSON.stringify({
        '1': otpCode // Template variable for OTP code
      }));

      // Create Basic Auth header
      const authHeader = btoa(`${this.accountSid}:${this.authToken}`);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${authHeader}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      const result = await response?.json();

      if (!response?.ok) {
        console.error('Twilio WhatsApp Template API error:', result);
        
        // Enhanced error handling for template-specific errors
        if (result?.code === 63016) {
          throw new Error('WhatsApp Template Error: The template is not approved or active. Please check your template status in Twilio Console.');
        } else if (result?.code === 63017) {
          throw new Error('WhatsApp Template Error: Template variables are invalid or missing. Please check the template configuration.');
        } else if (result?.code === 63015) {
          throw new Error('WhatsApp Template Error: Template not found. Please verify the template ID is correct.');
        } else if (result?.code === 21608) {
          throw new Error('WhatsApp Sandbox Error: Your phone number is not verified for WhatsApp sandbox. Please send "join <sandbox-keyword>" to the Twilio WhatsApp number first.');
        } else if (result?.code === 21211) {
          throw new Error('Invalid phone number format. Please check the number and try again.');
        } else if (result?.code === 20003) {
          throw new Error('Twilio authentication failed. Please verify your credentials.');
        } else if (result?.code === 21614) {
          throw new Error('WhatsApp message could not be sent. Please verify the recipient is available on WhatsApp.');
        } else {
          throw new Error(result?.message || `Twilio WhatsApp Template API error: ${response?.status}`);
        }
      }

      console.log('WhatsApp template message sent successfully:', result?.sid);
      
      return {
        success: true,
        messageSid: result?.sid,
        status: result?.status,
        to: result?.to,
        from: result?.from,
        dateCreated: result?.date_created,
        templateUsed: {
          id: this.otpTemplateId,
          name: this.otpTemplateName
        }
      };

    } catch (error) {
      console.error('Error sending WhatsApp template OTP:', error);
      
      // Enhanced error handling for template-specific issues
      if (error?.message?.includes('Template') || error?.message?.includes('63016') || error?.message?.includes('63017') || error?.message?.includes('63015')) {
        throw error; // Re-throw template-specific errors as-is
      } else if (error?.message?.includes('Permission to send an SMS') || error?.message?.includes('21608')) {
        throw new Error('WhatsApp sandbox not configured. Please verify your phone number in Twilio WhatsApp sandbox by sending "join <sandbox-keyword>" to the Twilio WhatsApp number.');
      } else if (error?.message?.includes('The \'To\' number')) {
        throw new Error('Invalid phone number format or number not verified in Twilio sandbox.');
      } else if (error?.message?.includes('authenticate') || error?.message?.includes('20003')) {
        throw new Error('Invalid Twilio credentials. Please check your Account SID and Auth Token.');
      } else if (error?.message?.includes('fetch')) {
        throw new Error('Network error: Unable to connect to Twilio. Please check your internet connection.');
      } else {
        throw new Error(`Failed to send WhatsApp template OTP: ${error.message}`);
      }
    }
  }

  /**
   * Send WhatsApp OTP using Twilio Verify service
   */
  async _sendVerifyWhatsAppOTP(phoneNumber) {
    try {
      const toNumber = phoneNumber?.replace(/\D/g, '')?.length === 10
        ? `+91${phoneNumber?.replace(/\D/g, '')}`
        : phoneNumber;

      const url = `https://verify.twilio.com/v2/Services/${this.verifyServiceSid}/Verifications`;
      const formData = new URLSearchParams();
      formData?.append('To', toNumber);
      formData?.append('Channel', 'whatsapp');

      const authHeader = btoa(`${this.accountSid}:${this.authToken}`);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${authHeader}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      const result = await response?.json();

      if (!response?.ok) {
        console.error('Twilio Verify API error:', result);
        throw new Error(result?.message || `Twilio Verify API error: ${response?.status}`);
      }

      return {
        success: true,
        sid: result?.sid,
        status: result?.status,
        to: result?.to,
        channel: result?.channel,
        serviceSid: this.verifyServiceSid
      };
    } catch (error) {
      console.error('Error sending Verify WhatsApp OTP:', error);
      throw error;
    }
  }

  /**
   * Verify WhatsApp OTP using Twilio Verify service
   */
  async checkWhatsAppOTP(phoneNumber, otpCode) {
    if (!this.isVerifyConfigured) {
      throw new Error('Twilio Verify service not configured. Please check environment variables.');
    }

    try {
      const toNumber = phoneNumber?.replace(/\D/g, '')?.length === 10
        ? `+91${phoneNumber?.replace(/\D/g, '')}`
        : phoneNumber;

      const url = `https://verify.twilio.com/v2/Services/${this.verifyServiceSid}/VerificationCheck`;
      const formData = new URLSearchParams();
      formData?.append('To', toNumber);
      formData?.append('Code', otpCode);

      const authHeader = btoa(`${this.accountSid}:${this.authToken}`);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${authHeader}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      const result = await response?.json();

      if (!response?.ok) {
        console.error('Twilio Verify check error:', result);
        throw new Error(result?.message || `Twilio Verify check error: ${response?.status}`);
      }

      return {
        success: result?.status === 'approved',
        status: result?.status,
        to: result?.to,
        channel: result?.channel
      };
    } catch (error) {
      console.error('Error verifying WhatsApp OTP via Verify:', error);
      throw error;
    }
  }

  /**
   * Test Twilio configuration
   */
  async testConfiguration() {
    if (!this.isConfigured) {
      return {
        success: false,
        error: 'Missing Twilio configuration',
        details: {
          accountSid: !!this.accountSid,
          authToken: !!this.authToken,
          whatsappFrom: !!this.whatsappFrom
        }
      };
    }

    try {
      // Test by fetching account information
      const url = `https://api.twilio.com/2010-04-01/Accounts/${this.accountSid}.json`;
      
      const authHeader = btoa(`${this.accountSid}:${this.authToken}`);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${authHeader}`,
        }
      });

      if (response?.ok) {
        const accountInfo = await response?.json();
        return {
          success: true,
          accountInfo: {
            friendlyName: accountInfo?.friendly_name,
            status: accountInfo?.status,
            type: accountInfo?.type
          }
        };
      } else {
        const error = await response?.json();
        return {
          success: false,
          error: error?.message || 'Invalid credentials',
          status: response?.status
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error?.message,
        details: 'Failed to connect to Twilio API'
      };
    }
  }

  /**
   * Get configuration help for missing environment variables
   */
  getConfigurationHelp() {
    return {
      title: 'WhatsApp Template Integration Setup',
      message: 'To enable WhatsApp template-based OTP functionality, add these environment variables to your .env file:',
      variables: [
        'VITE_TWILIO_ACCOUNT_SID - Your Twilio Account SID',
        'VITE_TWILIO_AUTH_TOKEN - Your Twilio Auth Token',
        'VITE_TWILIO_WHATSAPP_FROM - Your Twilio WhatsApp number (e.g., whatsapp:+14155238886)',
        'VITE_TWILIO_VERIFY_SERVICE_SID - Your Twilio Verify Service SID (for WhatsApp OTP)',
        'VITE_TWILIO_WHATSAPP_TEMPLATE_ID - Your approved WhatsApp template ID (optional, defaults to HX999835db369a33272322bd02699e7c7b)',
        'VITE_TWILIO_WHATSAPP_TEMPLATE_NAME - Your WhatsApp template name (optional, defaults to otp_verification)'
      ],
      instructions: [
        '1. Sign up for Twilio account at https://www.twilio.com',
        '2. Get credentials from Twilio Console Dashboard',
        '3. Set up WhatsApp Business API or Sandbox in Twilio Console',
        '4. Create and submit WhatsApp message template for approval',
        '5. Wait for template approval from WhatsApp',
        '6. IMPORTANT: Join the sandbox with your phone number by sending "join <sandbox-keyword>" to the Twilio WhatsApp number',
        '7. Add the environment variables to your .env file with VITE_ prefix',
        '8. Restart your development server',
        '9. Test the functionality'
      ],
      notes: [
        'This uses WhatsApp approved templates instead of freestyle messages',
        'Templates must be approved by WhatsApp before use',
        'Environment variables must have VITE_ prefix to work in React',
        'WhatsApp sandbox requires phone number verification before testing',
        'Template-based messaging is required for production WhatsApp usage'
      ],
      templateSetup: {
        title: 'WhatsApp Template Setup',
        currentTemplate: {
          id: this.otpTemplateId,
          name: this.otpTemplateName,
          description: 'OTP verification template for secure authentication'
        },
        steps: [
          '1. Go to Twilio Console > Messaging > Content Templates',
          '2. Create new WhatsApp template with name "otp_verification"',
          '3. Add template content with OTP variable placeholder',
          '4. Submit for WhatsApp approval',
          '5. Wait 24-48 hours for approval',
          '6. Update environment variables with template ID'
        ],
        templateExample: {
          name: 'otp_verification',
          content: 'Your verification code is {{1}}. This code will expire in 10 minutes. Do not share this code with anyone.',
          variables: ['OTP Code']
        }
      },
      troubleshooting: {
        title: 'Template-Specific Issues',
        issues: [
          {
            problem: 'Template not approved error (63016)',
            solution: 'Check template status in Twilio Console and wait for WhatsApp approval'
          },
          {
            problem: 'Template variables invalid (63017)',
            solution: 'Verify template variable format and ensure OTP is passed correctly'
          },
          {
            problem: 'Template not found (63015)',
            solution: 'Verify the template ID in environment variables matches your approved template'
          },
          {
            problem: 'Phone number not verified error',
            solution: 'Send "join <sandbox-keyword>" to your Twilio WhatsApp sandbox number'
          },
          {
            problem: 'Authentication failed',
            solution: 'Verify your TWILIO_ACCOUNT_SID and TWILIO_AUTH_TOKEN are correct'
          }
        ]
      }
    };
  }
}

// Create singleton instance
const twilioService = new TwilioService();

export default twilioService;
